import React from 'react';
import { Box, Paper, Typography, Button, CircularProgress, Slide } from '@mui/material';

/**
 * Floating Save/Discard bar shown while any field has an unsaved edit — the web
 * counterpart of the iOS product screen's floating edit bar. Save/Discard act
 * on every pending edit across all products.
 *
 * Props:
 *   changeCount  — number of edited fields
 *   productCount — number of products those edits span
 *   saving       — boolean
 *   onSave / onDiscard
 */
export default function EditBar({ changeCount, productCount, saving, onSave, onDiscard }) {
  const open = changeCount > 0;
  return (
    // Centered by a non-interactive full-width wrapper, since Slide owns the
    // Paper's transform and would clobber a translateX(-50%) centering.
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: (theme) => theme.zIndex.snackbar,
      }}
    >
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pl: 2.5,
            pr: 1,
            py: 1,
            borderRadius: 6,
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {changeCount} unsaved change{changeCount === 1 ? '' : 's'}
            {productCount > 1 ? ` across ${productCount} items` : ''}
          </Typography>
          <Button size="small" color="inherit" onClick={onDiscard} disabled={saving} sx={{ textTransform: 'none' }}>
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', borderRadius: 4, px: 2.5 }}
          >
            Save
          </Button>
        </Paper>
      </Slide>
    </Box>
  );
}
